import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PAGE_SIZE_SM } from '~constants/common';

import { useSearch } from '~libs/searchParams';

import { ClickableIcon, Toolbar } from '~components';
import { SearchBar } from '~components/form-fields/search';
import { faMagnifyingGlass, faUserNurse } from '~components/icon';
import { Row, TableView } from '~components/table-view';

import columnsFactory from '~clients/forms/care-plans/clientsWithCarePlansList';
import searchFields from '~clients/forms/care-plans/clientsWithCarePlansSearch';

import { useGetClientsSummaryQuery } from '~clients/actions/carePlans';

const ClientsWithCarePlans: React.FC = () => {
  const navigate = useNavigate();

  const [limit, setLimit] = useState(PAGE_SIZE_SM);
  const [searchBarOpen, setSearchBarOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearch('clients-with-care-plans-search', {});

  // Forward the search + status filters straight to the query. The endpoint applies them
  // server-side before pagination — client name via the standard first_name/surname iLike
  // filter, status via the status param — consistent with ClientsWithBudgets.
  const { data, isFetching } = useGetClientsSummaryQuery({ limit, offset: 0, ...searchParams });

  const rows = useMemo(() => (data?.rows ?? []) as Row[], [data]);
  const total = data?.total ?? 0;

  const columns = useMemo(
    () => columnsFactory((clientId: number) => navigate(`/clients/${clientId}/care-plans`)),
    [navigate],
  );

  return (
    <div className="p-2">
      <Toolbar
        id="clients-with-care-plans"
        leftCluster={
          // Visible client count for the load-more list — TableView only renders its own count in
          // 'pages' mode, so surface loaded-of-total here alongside the toolbar (per AC2).
          <span className="text-muted small" data-cy="clients-with-care-plans-count">
            Showing {rows.length} of {total} {total === 1 ? 'client' : 'clients'}
          </span>
        }
        rightCluster={
          <ClickableIcon
            className="me-2"
            icon={faMagnifyingGlass}
            id="clients-with-care-plans-search-button"
            onClick={() => setSearchBarOpen(!searchBarOpen)}
            toolTip="Toggle Search Bar"
          />
        }
      />
      <SearchBar
        isOpen={searchBarOpen}
        fields={searchFields}
        initialValues={searchParams}
        onSubmit={(values: Record<string, any>) => setSearchParams(values)}
      />
      <TableView
        id="clients-with-care-plans"
        data={rows}
        columns={columns}
        isLoading={isFetching}
        emptyIcon={faUserNurse}
        allowPagination={true}
        onLoadMore={() => setLimit((prev) => prev + PAGE_SIZE_SM)}
        showCount={true}
        totalCount={total}
        emptyMessage="No clients found"
        readonly
        striped
      />
    </div>
  );
};

export default ClientsWithCarePlans;
